/**
 * DoormanRexAI — pure behaviour tree for "Doorman Rex" Stage 2 boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * An 8-ft nightclub bouncer built like a brick outhouse.
 *
 * Phase 1 (HP > 50%): 3-attack round-robin
 *   0 → brass_knuckle  (close-range 3-hit combo)
 *   1 → shoulder_charge (telegraphed dash, any range)
 *   2 → staircase_shove (close-range neon-staircase knockdown)
 *
 * Phase 2 (HP ≤ 50%): faster cooldowns + extends to 4-attack cycle
 *   3 → ground_pound   (close-range ground shockwave)
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** Brass-knuckle 3-hit combo fires from close quarters. */
export const COMBO_RANGE = 90;
/** Staircase knockdown shove fires from close quarters. */
export const SHOVE_RANGE = 100;
/** Ground-pound shockwave fires from close quarters (phase 2). */
export const GROUND_POUND_RANGE = 110;

// ── Timing constants ─────────────────────────────────────────────────────────
/** Telegraph frames before the 3-hit combo connects. */
export const BRASS_KNUCKLE_WINDUP = 20;
/** Telegraph frames for the shoulder-charge dash (clearly telegraphed). */
export const CHARGE_WINDUP = 28;
/** Telegraph frames before the staircase shove. */
export const SHOVE_WINDUP = 16;
/** Telegraph frames before the ground-pound shockwave (phase 2). */
export const GROUND_POUND_WINDUP = 24;
/** Retreat frames after a combo or shove. */
export const BACKSTEP_FRAMES = 28;

// ── Phase threshold ───────────────────────────────────────────────────────────
export const PHASE_2_THRESHOLD = 0.5;

// ── Speed / cooldown tuning ──────────────────────────────────────────────────
/** Phase 2 movement and backstep speed multiplier. */
export const PHASE2_SPEED_SCALE = 1.3;

const BASE_COOLDOWN = 75;
const PHASE2_COOLDOWN_SCALE = 0.65;

// ── Types ─────────────────────────────────────────────────────────────────────
export type DoormanRexPhase = 1 | 2;

export type DoormanRexAttack =
  | 'brass_knuckle'
  | 'shoulder_charge'
  | 'staircase_shove'
  | 'ground_pound';

export type DoormanRexNode =
  | 'scan'
  | 'approach'
  | 'brass_knuckle_windup'
  | 'charge_windup'
  | 'shove_windup'
  | 'ground_pound_windup'
  | 'backstep';

export interface DoormanRexAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). Drives phase-transition checks each frame. */
  hpFraction: number;
}

export interface DoormanRexAIState {
  node: DoormanRexNode;
  phase: DoormanRexPhase;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  /** Round-robin index. Phase 1 wraps at 3; phase 2 wraps at 4. */
  attackCycle: number;
}

export interface DoormanRexAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: DoormanRexAttack | null;
  nextState: DoormanRexAIState;
}

export function initialDoormanRexState(): DoormanRexAIState {
  return {
    node: 'scan',
    phase: 1,
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    attackCycle: 0,
  };
}

/**
 * Advance the Doorman Rex behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns
 * hitboxes based on the output.
 */
export function tickDoormanRexAI(
  input: DoormanRexAIInput,
  state: DoormanRexAIState,
): DoormanRexAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialDoormanRexState());
  }

  // ── Phase resolution (re-derived every frame from live HP) ────────────────
  const phase: DoormanRexPhase = input.hpFraction <= PHASE_2_THRESHOLD ? 2 : 1;
  /** Cycle length: 3 in phase 1, 4 in phase 2 (adds ground_pound). */
  const cycleLen = phase === 2 ? 4 : 3;

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);

  // ── Hitstun: freeze animation, reset active states, keep ticking cooldown ─
  if (input.isHitstunned) {
    return noOp(facing, {
      ...state,
      phase,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
    });
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      const speed = phase === 2 ? PHASE2_SPEED_SCALE : 1.0;
      return {
        moveX: away.x * speed,
        moveY: away.y * speed,
        facing,
        wantsAttack: false,
        attackType: null,
        nextState: { ...state, phase, cooldown, backstepFrames },
      };
    }
    return noOp(facing, { ...state, phase, cooldown, node: 'scan', backstepFrames: 0 });
  }

  // ── BRASS KNUCKLE WINDUP ──────────────────────────────────────────────────
  if (state.node === 'brass_knuckle_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, cooldown, windup });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'brass_knuckle',
      nextState: {
        ...state,
        phase,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale, phase),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle, cycleLen),
      },
    };
  }

  // ── SHOULDER CHARGE WINDUP ────────────────────────────────────────────────
  if (state.node === 'charge_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, cooldown, windup });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'shoulder_charge',
      nextState: {
        ...state,
        phase,
        node: 'scan', // charge lands the boss where player is — no backstep needed
        cooldown: cooldownFor(input.aggressionScale, phase),
        windup: 0,
        attackCycle: nextCycle(state.attackCycle, cycleLen),
      },
    };
  }

  // ── STAIRCASE SHOVE WINDUP ────────────────────────────────────────────────
  if (state.node === 'shove_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, cooldown, windup });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'staircase_shove',
      nextState: {
        ...state,
        phase,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale, phase),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle, cycleLen),
      },
    };
  }

  // ── GROUND POUND WINDUP (phase 2 only) ───────────────────────────────────
  if (state.node === 'ground_pound_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, cooldown, windup });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'ground_pound',
      nextState: {
        ...state,
        phase,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale, phase),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle, cycleLen),
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ─────────────────────────────────
  const { dist } = toTarget(input);
  const { attackCycle } = state;

  if (cooldown === 0 && input.canAttack) {
    if (attackCycle === 0) {
      if (dist <= COMBO_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          cooldown,
          node: 'brass_knuckle_windup',
          windup: BRASS_KNUCKLE_WINDUP,
        });
      }
    } else if (attackCycle === 1) {
      // Shoulder charge fires at any range — telegraphed dash
      return noOp(facing, {
        ...state,
        phase,
        cooldown,
        node: 'charge_windup',
        windup: CHARGE_WINDUP,
      });
    } else if (attackCycle === 2) {
      if (dist <= SHOVE_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          cooldown,
          node: 'shove_windup',
          windup: SHOVE_WINDUP,
        });
      }
    } else if (attackCycle === 3 && phase === 2) {
      // Ground pound shockwave — phase 2 only
      if (dist <= GROUND_POUND_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          cooldown,
          node: 'ground_pound_windup',
          windup: GROUND_POUND_WINDUP,
        });
      }
    }
  }

  // ── Approach orbit position ───────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = (phase === 2 ? PHASE2_SPEED_SCALE : 1.0) + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: DoormanRexNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      nextState: {
        ...state,
        phase,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
      },
    };
  }

  return noOp(facing, { ...state, phase, cooldown, node: 'scan' });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number, phase: DoormanRexPhase): number {
  const phaseScale = phase === 2 ? PHASE2_COOLDOWN_SCALE : 1.0;
  return Math.round(BASE_COOLDOWN * phaseScale * (1 - aggressionScale * 0.5));
}

function toTarget(input: DoormanRexAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: DoormanRexAIState): DoormanRexAIOutput {
  return { moveX: 0, moveY: 0, facing, wantsAttack: false, attackType: null, nextState };
}

function nextCycle(c: number, len: number): number {
  return (c + 1) % len;
}
